import { useEffect, useMemo, useState } from 'react';
import { Button, Chip, FormControlLabel, Switch, Tooltip, Typography } from '@material-ui/core';
import { Link, Progress, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { useReleasesCall } from './useReleases';

type Service = { slug: string; pr: string; real: string; tag?: string; at?: string; cdUrl?: string };
type Plan = {
  key: string;
  url: string;
  name: string;
  jiraStatus: string;
  created: string;
  plannedAt?: string;
  team: string;
  type: string;
  version: string;
  versionUrl?: string;
  real: 'pendiente' | 'en curso' | 'desplegado' | 'falló' | 'revertido' | 'sin datos';
  services: Service[];
  lastDeployAt?: string;
  jiraAhead: boolean;
};

const when = (d?: string) => (d ? new Date(d).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const COLOR: Record<string, 'primary' | 'secondary' | 'default'> = { desplegado: 'primary', falló: 'secondary' };
const short = (slug: string) => slug.split('/')[1] ?? slug;
// Servicios con la versión del plan en prod (desplegada, o en verde pero Degraded en Argo): lo que el rollback revierte.
const revertible = (p: Plan) => p.services.filter(s => (s.real === 'desplegado' || s.real === 'falló') && s.tag);

const baseColumns: TableColumn<Plan>[] = [
  { title: 'Plan', field: 'key', render: p => <Link to={p.url}>{p.key}</Link> },
  { title: 'Nombre', field: 'name', render: p => (p.versionUrl ? <Link to={p.versionUrl}>{p.name}</Link> : p.name) },
  { title: 'Equipo', field: 'team' },
  { title: 'Estado en Jira', field: 'jiraStatus' },
  {
    title: 'Despliegue real',
    field: 'real',
    render: p => (
      <>
        <Chip size="small" color={COLOR[p.real] ?? 'default'} label={p.real} />
        {p.jiraAhead && (
          <Tooltip title="Jira ya lo da por desplegado, pero no todos sus servicios llegaron bien a prod">
            <Chip size="small" style={{ marginLeft: 4, background: '#f57c00', color: '#fff' }} label="Jira por delante" />
          </Tooltip>
        )}
      </>
    ),
  },
  { title: 'Planificado', field: 'plannedAt', render: p => when(p.plannedAt), defaultSort: 'desc' },
  { title: 'Desplegado', field: 'lastDeployAt', render: p => when(p.lastDeployAt) },
  { title: 'Servicios', field: 'services', render: p => p.services.length },
];

const ServiceList = ({ plan }: { plan: Plan }) => (
  <ul style={{ margin: '8px 0 8px 48px' }}>
    {plan.services.map(s => (
      <li key={s.pr}>
        <Link to={s.pr}>{s.slug.split('/')[1]}</Link> · <b>{s.real}</b>
        {s.tag && (
          <>
            {' '}
            · {s.cdUrl ? <Link to={s.cdUrl}>{s.tag}</Link> : s.tag} ({when(s.at)})
          </>
        )}
      </li>
    ))}
  </ul>
);

/** Plan Release de Jira (RMD) frente a lo que de verdad se desplegó. Solo lectura: el plan lo lleva Agile en Jira. */
export const PlanReleases = () => {
  const call = useReleasesCall();
  const [plans, setPlans] = useState<Plan[]>();
  const [error, setError] = useState<string>();
  const [onlyAhead, setOnlyAhead] = useState(false);
  const [rollback, setRollback] = useState<{ key: string; lines: string[] }>();
  const [busy, setBusy] = useState(false);

  // Rollback del plan: cada servicio vuelve a la versión anterior de prod con el rollback de siempre (aprobación y freeze
  // incluidos). Se omite el que ya no tiene en prod la versión del plan.
  const rollbackPlan = async (plan: Plan) => {
    const targets = revertible(plan);
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Rollback del plan ${plan.key}: ${targets.map(s => short(s.slug)).join(', ')} vuelven a su versión anterior en prod. ` +
        'Cada CD espera la aprobación de devops. Motivo (obligatorio):',
    );
    if (!reason?.trim()) return;
    setBusy(true);
    const lines: string[] = [];
    for (const s of targets) {
      try {
        const prod = (await call(`/rollback?slug=${encodeURIComponent(s.slug)}`)).environments?.prod;
        const to = prod?.previous?.[0]?.tag;
        if (prod?.current.tag !== s.tag) lines.push(`${short(s.slug)}: omitido, en prod está ${prod?.current.tag ?? '—'} y no ${s.tag}`);
        else if (!to) lines.push(`${short(s.slug)}: omitido, no hay una versión anterior en prod`);
        else {
          await call('/rollback', { slug: s.slug, tag: to, reason: `Rollback del plan ${plan.key}: ${reason.trim()}`.slice(0, 300) });
          lines.push(`${short(s.slug)}: ${s.tag} → ${to}, CD lanzado (espera aprobación)`);
        }
      } catch (e: any) {
        lines.push(`${short(s.slug)}: error, ${String(e?.message ?? e)}`);
      }
    }
    setRollback({ key: plan.key, lines });
    setBusy(false);
  };
  const columns: TableColumn<Plan>[] = [
    ...baseColumns,
    {
      title: '',
      field: 'key',
      sorting: false,
      render: p =>
        revertible(p).length > 0 && (p.real === 'falló' || p.real === 'desplegado') ? (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={busy}
            onClick={e => {
              e.stopPropagation();
              rollbackPlan(p);
            }}
          >
            Rollback del plan
          </Button>
        ) : null,
    },
  ];
  useEffect(() => {
    call('/plans').then(r => setPlans(r.plans), e => setError(String(e?.message ?? e)));
  }, [call]);
  const summary = useMemo(() => {
    if (!plans) return undefined;
    const done = plans.filter(p => /finalizado/i.test(p.jiraStatus));
    return {
      total: plans.length,
      done: done.length,
      doneDeployed: done.filter(p => p.real === 'desplegado').length,
      doneNoData: done.filter(p => p.real === 'sin datos').length,
      ahead: plans.filter(p => p.jiraAhead).length,
    };
  }, [plans]);
  if (error) return <WarningPanel title="No se pudo leer los Plan Release" message={error} />;
  if (!plans || !summary) return <Progress />;
  return (
    <>
      <Typography style={{ marginBottom: 8 }}>
        <b>{summary.total}</b> planes en 30 días · <b>{summary.done}</b> finalizados en Jira: {summary.doneDeployed} con todos sus msa en prod,{' '}
        {summary.doneNoData} sin msa que medir (SPA, IaC, librerías…) ·{' '}
        <b style={{ color: summary.ahead ? '#f57c00' : undefined }}>{summary.ahead} con Jira por delante de la realidad</b>
      </Typography>
      {rollback && (
        <WarningPanel severity="info" title={`Rollback del plan ${rollback.key}`} defaultExpanded>
          <ul style={{ margin: 0 }}>
            {rollback.lines.map(l => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </WarningPanel>
      )}
      <FormControlLabel
        control={<Switch checked={onlyAhead} onChange={e => setOnlyAhead(e.target.checked)} />}
        label="Solo los que Jira da por hechos sin estarlo"
      />
      <Table<Plan>
        title={`Plan Release (${onlyAhead ? summary.ahead : plans.length})`}
        subtitle="Jira (RMD) frente a GitHub: si cada PR se mergeó y si su CD de prod desplegó, espera aprobación o falló"
        options={{ paging: true, pageSize: 25, search: true, padding: 'dense' }}
        columns={columns}
        data={onlyAhead ? plans.filter(p => p.jiraAhead) : plans}
        detailPanel={({ rowData }) => <ServiceList plan={rowData} />}
      />
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
        Por cada PR del plan se toma el primer despliegue a prod (tag v…) posterior a su merge. «Falló» incluye el CD en verde que dejó la app
        Degraded en Argo CD; «revertido», el plan al que luego se le hizo rollback. «Sin datos»: el plan solo tiene repos sin CD de msa (SPA,
        IaC, librerías, móvil). El plan, sus estados y aprobaciones siguen en Jira.
      </Typography>
    </>
  );
};
